// Esqueletos de las pantallas del chofer (documento 07): lo que se ve mientras la app abre la base del celular o espera
// al servidor. Llevan el marco de la pantalla (título y pasos del día) y la forma de su contenido, así nada salta al
// llegar los datos. Casi siempre los datos salen del celular en un instante: la zona espera 150 ms antes de mostrarse
// (ZonaCargando), así sin señal o con un celular rápido no hay ningún parpadeo.
import { cx, Esqueleto, EsqueletoRenglon, EsqueletoTexto, Icono, Tarjeta, ZonaCargando } from '@reparto/ui';
import type { ReactNode } from 'react';
import { type Ancho, Marco, type Paso } from './Marco';
import { MarcoAcceso } from './MarcoAcceso';
import { usePantallaAncha } from './pantalla';

/** Tres datos (Estadistica) en gris: valor y etiqueta, con el mismo alto. */
function Estadisticas() {
  return (
    <div className="grid grid-cols-3 gap-2">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="rounded-2xl border border-stone-200 bg-white p-2 dark:border-stone-800 dark:bg-stone-900"
        >
          <div className="flex h-[22.5px] items-center justify-center">
            <Esqueleto className="h-4 w-12 rounded-md" />
          </div>
          <div className="flex h-[16.5px] items-center justify-center">
            <Esqueleto className="h-2.5 w-14 rounded-full" />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Píldoras del alto de un Chip. */
function Chips({ anchos }: { anchos: string[] }) {
  return (
    <div className="mt-1 flex flex-wrap gap-1.5">
      {anchos.map((a, i) => (
        <Esqueleto key={i} className={cx('h-5 rounded-full', a)} />
      ))}
    </div>
  );
}

/**
 * El pie con los botones de la pantalla, cada uno con su alto (`h-14` el grande, `h-12` el común): uno debajo del otro
 * o, con `lado`, uno al lado del otro.
 */
function PieBoton({ botones = ['h-14'], lado }: { botones?: string[]; lado?: boolean }) {
  return (
    <div aria-hidden="true" className={cx('pb-1', lado ? 'grid grid-cols-2 gap-2' : 'space-y-2')}>
      {botones.map((alto, i) => (
        <Esqueleto key={i} className={cx(alto, 'w-full rounded-full')} />
      ))}
    </div>
  );
}

/** Pantalla con su marco y el contenido en gris dentro de una zona que avisa al lector de pantalla. */
function PantallaEsqueleto({
  titulo,
  paso,
  volver,
  ancho,
  pie,
  texto,
  children,
}: {
  titulo: string;
  paso?: Paso;
  volver?: string;
  ancho?: Ancho;
  pie?: ReactNode;
  texto: string;
  children: ReactNode;
}) {
  // El subtítulo va vacío pero ocupa su renglón: al llegar los datos el título no se mueve.
  return (
    <Marco titulo={titulo} subtitulo={'\u00a0'} paso={paso} volver={volver} ancho={ancho} pie={pie}>
      <ZonaCargando texto={texto}>{children}</ZonaCargando>
    </Marco>
  );
}

const ANCHOS_NOMBRE = ['62%', '48%', '70%', '55%', '66%', '44%'];

/** Un pedido de la carga: el casillero para tildar, número y zona, cliente y los chips de bultos y cobro. */
function FilaCarga({ i }: { i: number }) {
  return (
    <Tarjeta relleno="none" className="flex items-stretch">
      <div className="grid w-16 shrink-0 place-items-center">
        <Esqueleto className="h-10 w-10 rounded-xl" />
      </div>
      <div className="min-w-0 flex-1 py-3 pr-3">
        <EsqueletoRenglon tamano="xs" ancho="38%" />
        <EsqueletoRenglon tamano="base" ancho={ANCHOS_NOMBRE[i % ANCHOS_NOMBRE.length]} />
        <Chips anchos={i % 3 === 2 ? ['w-20', 'w-32'] : ['w-20', 'w-32', 'w-28']} />
      </div>
    </Tarjeta>
  );
}

/** Una parada de la lista (armar ruta y ruta): el número, cliente, zona y bultos, y los chips de horario. */
function FilaParada({ i, flechas }: { i: number; flechas?: boolean }) {
  return (
    <Tarjeta relleno="sm" className="flex items-center gap-3">
      <Esqueleto className="h-9 w-9 shrink-0 rounded-full" />
      <div className="min-w-0 flex-1">
        <EsqueletoRenglon tamano="base" ancho={ANCHOS_NOMBRE[i % ANCHOS_NOMBRE.length]} />
        <EsqueletoRenglon tamano="xs" ancho="72%" />
        <Chips anchos={i % 2 ? ['w-16'] : ['w-16', 'w-24']} />
      </div>
      {flechas ? <div className="h-20 w-10 shrink-0" /> : <div className="w-6 shrink-0" />}
    </Tarjeta>
  );
}

/** P2 · Carga: el resumen (pedidos, bultos, a cobrar y ocupación) y la lista de pedidos para tildar. */
export function EsqueletoCarga() {
  return (
    <PantallaEsqueleto
      titulo="Carga"
      paso="carga"
      ancho="amplio"
      pie={<PieBoton botones={['h-12', 'h-12']} lado />}
      texto="Cargando los pedidos…"
    >
      <div className="space-y-3 p-4 lg:grid lg:grid-cols-[22rem_minmax(0,1fr)] lg:items-start lg:gap-5 lg:space-y-0">
        <Tarjeta className="space-y-3">
          <Estadisticas />
          <div>
            <div className="mb-1 flex h-4 items-center justify-between">
              <Esqueleto className="h-2.5 w-28 rounded-full" />
              <Esqueleto className="h-2.5 w-24 rounded-full" />
            </div>
            <Esqueleto className="h-1.5 w-full rounded-full" />
          </div>
        </Tarjeta>
        <div className="space-y-3">
          {Array.from({ length: 5 }, (_, i) => (
            <FilaCarga key={i} i={i} />
          ))}
        </div>
      </div>
    </PantallaEsqueleto>
  );
}

/** P3 · Armar ruta: el resumen con los botones del mapa y las paradas en orden. */
export function EsqueletoArmarRuta() {
  return (
    <PantallaEsqueleto
      titulo="Armar ruta"
      paso="ruta"
      pie={<PieBoton botones={['h-14', 'h-14']} />}
      texto="Cargando la ruta…"
    >
      <div className="space-y-3 p-4">
        <Tarjeta className="space-y-3">
          <Estadisticas />
          <div className="grid grid-cols-2 gap-2">
            <Esqueleto className="h-9 rounded-full" />
            <Esqueleto className="h-9 rounded-full" />
          </div>
        </Tarjeta>
        <div className="space-y-2">
          {Array.from({ length: 5 }, (_, i) => (
            <FilaParada key={i} i={i} flechas />
          ))}
        </div>
      </div>
    </PantallaEsqueleto>
  );
}

/** P4 · Ruta: el avance del día y las paradas. */
export function EsqueletoRuta() {
  return (
    <PantallaEsqueleto titulo="Ruta del día" paso="entrega" pie={<PieBoton />} texto="Cargando la ruta…">
      <div className="space-y-3 p-4">
        <Tarjeta className="space-y-3">
          <Estadisticas />
          <Esqueleto className="h-1.5 w-full rounded-full" />
          <div className="flex h-[18px] items-center justify-between">
            <Esqueleto className="h-2.5 w-40 rounded-full" />
            <Esqueleto className="h-2.5 w-14 rounded-full" />
          </div>
        </Tarjeta>
        <div className="space-y-2">
          {Array.from({ length: 5 }, (_, i) => (
            <FilaParada key={i} i={i} />
          ))}
        </div>
      </div>
    </PantallaEsqueleto>
  );
}

/** P5 · Parada: el cliente con sus botones, los bultos y el cobro. */
export function EsqueletoParada() {
  return (
    <PantallaEsqueleto
      titulo="Parada"
      paso="entrega"
      volver="/ruta"
      ancho="amplio"
      pie={<PieBoton botones={['h-14', 'h-12']} />}
      texto="Cargando la parada…"
    >
      <div className="space-y-3 p-4 lg:grid lg:grid-cols-2 lg:items-start lg:gap-4 lg:space-y-0">
        <div className="space-y-3">
          <Tarjeta className="space-y-2">
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <EsqueletoRenglon tamano="xs" ancho="45%" />
                <EsqueletoRenglon tamano="lg" ancho="70%" />
                <EsqueletoRenglon ancho="40%" />
              </div>
              <Esqueleto className="h-12 w-12 shrink-0 rounded-full" />
              <Esqueleto className="h-12 w-12 shrink-0 rounded-full" />
            </div>
            <EsqueletoRenglon ancho="80%" />
            <Chips anchos={['w-24', 'w-28']} />
          </Tarjeta>
          {/* "Llegué al cliente" y "Avisar al depósito". */}
          <Esqueleto className="h-12 w-full rounded-full" />
          <Esqueleto className="h-12 w-full rounded-full" />
          <div>
            <EsqueletoRenglon ancho="8rem" className="mb-2 px-1" />
            <Tarjeta relleno="none" className="divide-y divide-stone-100 dark:divide-stone-800">
              {[0, 1].map((i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <EsqueletoRenglon tamano="base" ancho={i ? '55%' : '68%'} />
                    <EsqueletoRenglon tamano="xs" ancho="50%" />
                  </div>
                  <Esqueleto className="h-[46px] w-[130px] shrink-0 rounded-2xl" />
                </div>
              ))}
            </Tarjeta>
          </div>
        </div>
        <div>
          <EsqueletoRenglon ancho="4rem" className="mb-2 px-1" />
          <Tarjeta className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              {[0, 1, 2, 3].map((i) => (
                <Esqueleto key={i} className="h-12 rounded-2xl" />
              ))}
            </div>
            <div>
              <EsqueletoRenglon tamano="xs" ancho="55%" className="mb-1" />
              <Esqueleto className="h-14 w-full rounded-2xl" />
            </div>
          </Tarjeta>
        </div>
      </div>
    </PantallaEsqueleto>
  );
}

/** Una tarjeta con título y filas de etiqueta y valor (los cobros, los bultos, las paradas). */
function TarjetaFilas({ filas, angosta }: { filas: number; angosta?: boolean }) {
  return (
    <Tarjeta>
      <EsqueletoRenglon ancho="5rem" className="mb-1" />
      {Array.from({ length: filas }, (_, i) => (
        <div key={i} className="flex h-8 items-center justify-between gap-3">
          <Esqueleto
            className={cx('h-3 rounded-full', angosta ? 'w-16' : ['w-20', 'w-10', 'w-24', 'w-28', 'w-40'][i % 5])}
          />
          <Esqueleto className={cx('h-3 rounded-full', angosta ? 'w-6' : 'w-20')} />
        </div>
      ))}
    </Tarjeta>
  );
}

/** P6 · Cierre: efectivo a rendir, cobros, bultos y paradas, y el detalle por parada. */
export function EsqueletoCierre() {
  return (
    <PantallaEsqueleto titulo="Cierre" paso="cierre" ancho="amplio" pie={<PieBoton />} texto="Cargando la rendición…">
      <div className="space-y-3 p-4 lg:grid lg:grid-cols-2 lg:items-start lg:gap-4 lg:space-y-0">
        <div className="space-y-3">
          <Tarjeta>
            <EsqueletoRenglon tamano="xs" ancho="8rem" />
            <EsqueletoRenglon tamano="3xl" ancho="11rem" />
          </Tarjeta>
          <TarjetaFilas filas={6} />
          <div className="grid grid-cols-2 gap-3">
            <TarjetaFilas filas={3} angosta />
            <TarjetaFilas filas={3} angosta />
          </div>
        </div>
        <Tarjeta relleno="none">
          <EsqueletoRenglon ancho="8rem" className="px-4 pt-3 pb-1" />
          <ul className="divide-y divide-stone-100 dark:divide-stone-800">
            {Array.from({ length: 4 }, (_, i) => (
              <li key={i} className="flex items-center gap-3 px-4 py-2.5">
                <Esqueleto className="h-3 w-4 rounded-full" />
                <div className="min-w-0 flex-1">
                  <EsqueletoRenglon ancho={ANCHOS_NOMBRE[i % ANCHOS_NOMBRE.length]} />
                  <EsqueletoRenglon tamano="xs" ancho="35%" />
                </div>
                <Esqueleto className="h-3 w-20 rounded-full" />
              </li>
            ))}
          </ul>
        </Tarjeta>
      </div>
    </PantallaEsqueleto>
  );
}

/** La sección de la impresora de Perfil mientras se descarga su código. */
export function EsqueletoImpresora() {
  return (
    <Tarjeta className="space-y-3" aria-hidden="true">
      <div className="flex items-center justify-between gap-2">
        <EsqueletoRenglon tamano="base" ancho="5.5rem" />
        <Esqueleto className="h-5 w-28 rounded-full" />
      </div>
      <div>
        <EsqueletoRenglon ancho="100%" />
        <EsqueletoRenglon ancho="60%" />
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <Esqueleto className="h-12 rounded-full" />
        <Esqueleto className="h-12 rounded-full" />
      </div>
    </Tarjeta>
  );
}

/** P7 · Perfil: el chofer, la sincronización, la impresora, el tema y los botones. */
export function EsqueletoPerfil() {
  return (
    <PantallaEsqueleto titulo="Perfil" volver="/" texto="Cargando el perfil…">
      <div className="space-y-3 p-4">
        <Tarjeta className="flex items-center gap-3">
          <Esqueleto className="h-14 w-14 shrink-0 rounded-full" />
          <div className="min-w-0 flex-1">
            <EsqueletoRenglon tamano="lg" ancho="50%" />
            <EsqueletoRenglon ancho="35%" />
            <EsqueletoRenglon tamano="xs" ancho="45%" />
          </div>
        </Tarjeta>
        <Tarjeta className="space-y-3">
          <div className="flex items-center justify-between">
            <EsqueletoRenglon tamano="base" ancho="8rem" />
            <Esqueleto className="h-5 w-16 rounded-full" />
          </div>
          <EsqueletoRenglon tamano="xs" ancho="40%" />
          <EsqueletoRenglon ancho="75%" />
          <Esqueleto className="h-12 w-full rounded-full" />
        </Tarjeta>
        <EsqueletoImpresora />
        <Tarjeta className="space-y-2">
          <EsqueletoRenglon tamano="base" ancho="3.5rem" />
          <Esqueleto className="h-12 w-full rounded-full" />
        </Tarjeta>
      </div>
    </PantallaEsqueleto>
  );
}

/** Mapa de la ruta a pantalla completa mientras se descarga su código. */
export function EsqueletoMapa({ pantalla }: { pantalla?: boolean }) {
  const mapa = (
    <Esqueleto className="grid h-full w-full place-items-center rounded-none">
      <Icono nombre="map" tamano={44} className="text-stone-300 dark:text-stone-700" />
    </Esqueleto>
  );
  if (!pantalla)
    return (
      <ZonaCargando texto="Abriendo el mapa…" className="h-full">
        {mapa}
      </ZonaCargando>
    );
  return (
    <Marco titulo="Mapa de la ruta" subtitulo={'\u00a0'} volver="/ruta" ancho="completo">
      <ZonaCargando texto="Abriendo el mapa…" className="h-full">
        {mapa}
      </ZonaCargando>
    </Marco>
  );
}

function TarjetasMoviles() {
  return [0, 1, 2].map((i) => (
    <Tarjeta key={i} className="flex items-center gap-3">
      <Esqueleto className="h-12 w-12 shrink-0 rounded-2xl" />
      <div className="min-w-0 flex-1">
        <EsqueletoRenglon tamano="base" ancho={ANCHOS_NOMBRE[i]} />
        <EsqueletoRenglon tamano="xs" ancho="80%" />
      </div>
      <Esqueleto className="h-5 w-20 shrink-0 rounded-full" />
    </Tarjeta>
  ));
}

/** Las tarjetas de los móviles mientras llega la lista del servidor. */
export function EsqueletoMoviles() {
  return (
    <ZonaCargando texto="Buscando móviles…" className="flex flex-col gap-3">
      <TarjetasMoviles />
    </ZonaCargando>
  );
}

// ---------- Acceso ----------

/** Un campo de las pantallas de acceso: la etiqueta y el casillero de 48 px (Campo y CampoClave). */
function CampoAcceso({ etiqueta, ayuda }: { etiqueta: string; ayuda?: boolean }) {
  return (
    <div>
      <EsqueletoRenglon tamano="xs" ancho={etiqueta} className="mb-1.5" />
      <Esqueleto className="h-12 w-full rounded-2xl" />
      {ayuda ? <EsqueletoRenglon tamano="xs" ancho="85%" className="mt-1" /> : null}
    </div>
  );
}

/**
 * El formulario de una pantalla de acceso en gris: el título, la explicación, el correo de la cuenta (`recuadro`, al
 * elegir la contraseña), los campos, el botón grande y, en la de entrar, "¿Olvidaste tu contraseña?".
 */
export function FormularioAccesoGris({
  texto,
  campos = 1,
  explicacion = 2,
  recuadro,
  ayuda,
  olvide,
}: {
  texto: string;
  campos?: number;
  explicacion?: number;
  recuadro?: boolean;
  ayuda?: boolean;
  olvide?: boolean;
}) {
  return (
    <ZonaCargando texto={texto} className="flex flex-col gap-4">
      <div>
        <EsqueletoRenglon tamano="xl" ancho="13rem" className="max-w-full" />
        {explicacion ? <EsqueletoTexto renglones={explicacion} className="mt-1" /> : null}
        {recuadro ? <Esqueleto className="mt-2 h-12 w-full rounded-2xl" /> : null}
      </div>
      {Array.from({ length: campos }, (_, i) => (
        <CampoAcceso key={i} etiqueta={i ? '5.5rem' : '3.5rem'} ayuda={ayuda} />
      ))}
      <Esqueleto className="h-14 w-full rounded-full" />
      {olvide ? (
        <div className="flex min-h-11 items-center justify-center">
          <Esqueleto className="h-3.5 w-44 rounded-full" />
        </div>
      ) : null}
    </ZonaCargando>
  );
}

/** P0 · El acceso mientras se lee la base del celular: la marca y el formulario de la pantalla en gris. */
export function EsqueletoAcceso({ ruta }: { ruta: string }) {
  if (ruta !== '/acceso')
    return (
      <MarcoAcceso compacto>
        <FormularioAccesoGris texto="Abriendo la app…" explicacion={1} />
      </MarcoAcceso>
    );
  return (
    <MarcoAcceso>
      <FormularioAccesoGris texto="Abriendo la app…" campos={2} olvide />
      <div aria-hidden="true" className="mt-6 border-t border-stone-200 pt-4 dark:border-stone-800">
        <div className="flex min-h-11 items-center justify-center">
          <Esqueleto className="h-3 w-48 rounded-full" />
        </div>
      </div>
    </MarcoAcceso>
  );
}

// ---------- Chat ----------

/** El fondo de la conversación (chat.css llega con el chat, que se carga aparte: acá va solo el color). */
const FONDO_CHAT = 'bg-[#efeae2] dark:bg-stone-950';

/** Las conversaciones de la lista (Fila: px-4 py-3 y el avatar de 52). */
export function EsqueletoListaChat() {
  return (
    <ZonaCargando texto="Cargando el chat…">
      <ul className="divide-y divide-stone-100 dark:divide-stone-800/70">
        {[0, 1, 2, 3].map((i) => (
          <li key={i} className="flex items-center gap-3 px-4 py-3">
            <Esqueleto className="h-[52px] w-[52px] shrink-0 rounded-full" />
            <div className="min-w-0 flex-1">
              <div className="flex h-[25.5px] items-center gap-2">
                <Esqueleto className="h-4 rounded-full" style={{ width: ANCHOS_NOMBRE[i] }} />
                <Esqueleto className="ml-auto h-2.5 w-9 shrink-0 rounded-full" />
              </div>
              <div className="mt-0.5 flex h-[22.5px] items-center">
                <Esqueleto className="h-3 rounded-full" style={{ width: ['80%', '64%', '72%', '58%'][i] }} />
              </div>
            </div>
          </li>
        ))}
      </ul>
    </ZonaCargando>
  );
}

/** Burbujas de un lado y del otro (las propias, verdes, a la derecha). */
const BURBUJAS: Array<{ propio?: boolean; renglones: number; ancho: string }> = [
  { renglones: 2, ancho: '70%' },
  { renglones: 1, ancho: '45%' },
  { propio: true, renglones: 1, ancho: '50%' },
  { renglones: 3, ancho: '78%' },
  { propio: true, renglones: 2, ancho: '66%' },
  { renglones: 1, ancho: '38%' },
  { propio: true, renglones: 1, ancho: '42%' },
];

/** El día y las burbujas (17 px, leading-snug) con la hora abajo a la derecha, como Burbuja. */
function MensajesGris() {
  return (
    <>
      <div className="my-2 flex justify-center">
        <Esqueleto className="h-7 w-20 rounded-lg" />
      </div>
      {BURBUJAS.map((b, i) => (
        <div key={i} className={cx('my-0.5 flex', b.propio ? 'justify-end pl-10' : 'justify-start pr-10')}>
          <div
            className={cx(
              'max-w-[min(85%,34rem)] rounded-2xl px-2.5 pt-1.5 pb-1 shadow-sm',
              b.propio ? 'rounded-tr-md bg-[#d9fdd3] dark:bg-primary-900' : 'rounded-tl-md bg-white dark:bg-stone-800',
            )}
            style={{ width: b.ancho }}
          >
            {Array.from({ length: b.renglones }, (_, r) => (
              <div key={r} className="flex h-[23.375px] items-center">
                <Esqueleto
                  className="h-3.5 rounded-full"
                  style={{ width: b.renglones > 1 && r === b.renglones - 1 ? '60%' : '100%' }}
                />
              </div>
            ))}
            <div className="flex h-[18px] items-center justify-end">
              <Esqueleto className="h-2.5 w-9 rounded-full" />
            </div>
          </div>
        </div>
      ))}
    </>
  );
}

/** Los mensajes de una conversación abierta mientras llegan del servidor. */
export function EsqueletoMensajes() {
  return (
    <ZonaCargando texto="Cargando los mensajes…">
      <MensajesGris />
    </ZonaCargando>
  );
}

/** El compositor en gris: adjuntar, el campo del mensaje y el botón redondo (Escribir). */
function EscribirGris() {
  return (
    <div className="seguro-abajo border-t border-black/5 bg-stone-100/95 px-2 pt-2 dark:border-white/5 dark:bg-stone-900/95">
      <div className="mx-auto flex max-w-3xl items-end gap-2 pb-1">
        <Esqueleto className="h-[52px] w-[52px] shrink-0 rounded-full" />
        <div className="flex h-12 min-w-0 flex-1 items-center rounded-3xl bg-white px-4 dark:bg-stone-800">
          <EsqueletoRenglon tamano="base" ancho="6rem" />
        </div>
        <Esqueleto className="h-[52px] w-[52px] shrink-0 rounded-full" />
      </div>
    </div>
  );
}

/** Una conversación en gris: los mensajes y el compositor y, al lado de la lista, la cabecera. */
export function PanelConversacionGris({
  conCabecera,
  texto = 'Abriendo el chat…',
}: {
  conCabecera?: boolean;
  texto?: string;
}) {
  return (
    <ZonaCargando texto={texto} className={cx('flex h-full min-h-0 flex-col', FONDO_CHAT)}>
      {conCabecera ? (
        <div className="flex items-center gap-3 border-b border-stone-200 bg-white px-4 py-2 dark:border-stone-800 dark:bg-stone-900">
          <Esqueleto className="h-10 w-10 shrink-0 rounded-full" />
          <div className="min-w-0 flex-1">
            <EsqueletoRenglon tamano="base" ancho="9rem" />
            <EsqueletoRenglon tamano="xs" ancho="6rem" />
          </div>
        </div>
      ) : null}
      <div className="min-h-0 flex-1 overflow-hidden">
        <div className="mx-auto max-w-3xl px-2 pt-2 pb-3 sm:px-4">
          <MensajesGris />
        </div>
      </div>
      <EscribirGris />
    </ZonaCargando>
  );
}

/** La lista de conversaciones con su marco, mientras se abre (o se descarga el código del chat). */
export function PantallaListaChat() {
  return (
    <PantallaEsqueleto titulo="Chat" volver="/" texto="Abriendo el chat…">
      <EsqueletoListaChat />
    </PantallaEsqueleto>
  );
}

/**
 * Una conversación mientras se abre: en el celular, a pantalla completa; en pantallas anchas, al lado de la lista
 * (como ConversacionChat).
 */
export function PantallaConversacionChat({ texto }: { texto?: string }) {
  const ancha = usePantallaAncha();
  if (ancha)
    return (
      <Marco
        titulo="Chat"
        subtitulo={' '}
        volver="/"
        lateral={
          <div className="h-full overflow-hidden rounded-2xl border border-stone-200 dark:border-stone-800">
            <PanelConversacionGris conCabecera texto={texto} />
          </div>
        }
      >
        <EsqueletoListaChat />
      </Marco>
    );
  return (
    <Marco titulo="Chat" subtitulo={' '} volver="/chat" ancho="completo">
      <PanelConversacionGris texto={texto} />
    </Marco>
  );
}

// ---------- Guía de uso ----------

/**
 * La guía de uso (/uso) mientras llega su índice: la descripción y el paso elegido (en pantallas anchas, la lista de
 * pasos), su título y su texto, y la captura del celular (390 × 844) con la leyenda al lado.
 */
export function EsqueletoGuia() {
  return (
    <ZonaCargando
      texto="Abriendo la guía…"
      className="lg:grid lg:grid-cols-[16rem_minmax(0,1fr)] lg:items-start lg:gap-8"
    >
      <div className="mb-5 lg:mb-0">
        <EsqueletoTexto renglones={2} className="mb-3 lg:hidden" />
        <EsqueletoTexto renglones={4} className="mb-3 hidden lg:block" />
        <div className="lg:hidden">
          <EsqueletoRenglon tamano="xs" ancho="2.5rem" className="mb-1.5" />
          <Esqueleto className="h-12 w-full rounded-2xl" />
        </div>
        <div className="hidden space-y-0.5 lg:block">
          {Array.from({ length: 12 }, (_, i) => (
            <div key={i} className="flex items-center gap-2.5 px-2.5 py-2">
              <Esqueleto className="h-6 w-6 shrink-0 rounded-full" />
              <Esqueleto className="h-3 rounded-full" style={{ width: ANCHOS_NOMBRE[i % ANCHOS_NOMBRE.length] }} />
            </div>
          ))}
        </div>
      </div>
      <div className="min-w-0">
        <div className="flex h-4 items-center gap-3">
          <Esqueleto className="h-2.5 w-20 rounded-full" />
          <Esqueleto className="h-1.5 max-w-40 flex-1 rounded-full" />
        </div>
        <EsqueletoRenglon tamano="2xl" ancho="60%" className="mt-1.5" />
        <EsqueletoTexto renglones={2} tamano="base" className="mt-2 max-w-prose" />
        <EsqueletoRenglon ancho="70%" className="mt-2 max-w-prose" />
        <div className="mt-4 md:flex md:items-start md:gap-6">
          <Esqueleto className="mx-auto aspect-[390/844] w-full max-w-[360px] shrink-0 rounded-2xl md:mx-0" />
          <div className="hidden min-w-0 flex-1 flex-col gap-2 md:flex">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className="flex gap-3 rounded-2xl border border-stone-200 bg-white p-3 dark:border-stone-800 dark:bg-stone-900"
              >
                <Esqueleto className="h-6 w-6 shrink-0 rounded-full" />
                <div className="min-w-0 flex-1">
                  <EsqueletoRenglon tamano="base" ancho="45%" />
                  <EsqueletoTexto renglones={2} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </ZonaCargando>
  );
}

// ---------- Escáner de la carga ----------

/** El escáner de la carga mientras se descarga su código: la cabecera, la cámara, el resumen y los artículos. */
export function EsqueletoEscaner() {
  return (
    <div className="fixed inset-0 z-50 bg-stone-50 dark:bg-stone-950">
      <ZonaCargando texto="Abriendo el escáner…" className="mx-auto flex h-full max-w-2xl flex-col">
        <div className="seguro-arriba bg-stone-950 text-white">
          <div className="flex items-center gap-2 px-3 py-2">
            <div className="min-w-0 flex-1 px-1">
              <div className="text-[17px] leading-tight font-medium">Escanear carga</div>
              <div className="truncate text-xs text-stone-300">Abriendo la cámara…</div>
            </div>
            <span className="h-9 w-20 rounded-full bg-amber-400/40" />
          </div>
        </div>
        <div className="aspect-[4/3] max-h-[34vh] w-full shrink-0 bg-black" />
        <div className="min-h-0 flex-1 space-y-3 overflow-hidden p-3">
          <Tarjeta className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <EsqueletoRenglon ancho="45%" />
              <Esqueleto className="h-2.5 w-24 rounded-full" />
            </div>
            <Esqueleto className="h-2 w-full rounded-full" />
          </Tarjeta>
          <ul className="divide-y divide-stone-100 overflow-hidden rounded-3xl border border-stone-200 bg-white dark:divide-stone-800 dark:border-stone-800 dark:bg-stone-900">
            {[0, 1, 2, 3].map((i) => (
              <li key={i} className="flex items-center gap-3 px-4 py-2.5">
                <Esqueleto className="h-6 w-6 shrink-0 rounded-full" />
                <div className="min-w-0 flex-1">
                  <EsqueletoRenglon ancho={ANCHOS_NOMBRE[i]} />
                  <EsqueletoRenglon tamano="xs" ancho="35%" />
                </div>
                <Esqueleto className="h-4 w-10 shrink-0 rounded-full" />
              </li>
            ))}
          </ul>
        </div>
      </ZonaCargando>
    </div>
  );
}

/** Lo que se ve al abrir la app mientras se lee la base del celular: el esqueleto de la pantalla a la que se va. */
export function PantallaCargando({ ruta }: { ruta: string }) {
  if (ruta === '/chat') return <PantallaListaChat />;
  if (ruta.startsWith('/chat/')) return <PantallaConversacionChat />;
  if (ruta.startsWith('/parada/')) return <EsqueletoParada />;
  if (ruta === '/ruta/armar') return <EsqueletoArmarRuta />;
  if (ruta === '/ruta') return <EsqueletoRuta />;
  if (ruta === '/cierre') return <EsqueletoCierre />;
  if (ruta === '/perfil') return <EsqueletoPerfil />;
  if (ruta === '/mapa') return <EsqueletoMapa pantalla />;
  if (ruta === '/carga') return <EsqueletoCarga />;
  return <PantallaNeutra />;
}

/**
 * Elegir el móvil o una dirección sin pantalla propia (la app se abre en "/"): el marco con una lista neutra. También
 * mientras se confirma la sesión al volver de Google, Apple o Facebook (`texto`: "Entrando con Google…").
 */
export function PantallaNeutra({ texto = 'Abriendo la app…' }: { texto?: string }) {
  return (
    <PantallaEsqueleto titulo="Reparto" texto={texto}>
      <div className="space-y-3 p-4">
        <TarjetasMoviles />
      </div>
    </PantallaEsqueleto>
  );
}
